import { fetchApi } from '@/services/api-client';

export const PARENTS_PATH = '/admin/parents';

export type ParentRelationship =
  | 'mother'
  | 'father'
  | 'guardian'
  | 'grandmother'
  | 'grandfather'
  | 'stepmother'
  | 'stepfather'
  | 'aunt'
  | 'uncle'
  | 'other';

export type ParentChildRef = {
  id: string;
  displayName: string;
  gradeLevel: number;
};

export type ParentAdminRow = {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  phone: string | null;
  relationship: ParentRelationship | null;
  address: string | null;
  active: boolean;
  accountStatus: 'invited' | 'active';
  children: ParentChildRef[];
  personId: string | null;
  /** null when the parent has never signed in. */
  lastLoginAt: string | null;
  createdAt: string;
  occupation: string | null;
  /** YYYY-MM-DD. */
  dateOfBirth: string | null;
  /** ISO 3166-1 alpha-2, in the parent's own order. */
  nationalities: string[];
  /** Which language this parent is written to in communications. */
  preferredLanguage: string;
};

export type CreateParentData = {
  fullName: string;
  email: string;
  phone?: string;
  relationship?: ParentRelationship;
  address?: string;
  studentIds: string[];
  personId?: string;
  occupation?: string | null;
  dateOfBirth?: string | null;
  nationalities?: string[];
  preferredLanguage?: string;
};

export type UpdateParentData = {
  email?: string;
  fullName?: string;
  phone?: string | null;
  relationship?: ParentRelationship | null;
  address?: string | null;
  studentIds?: string[];
  personId?: string | null;
  occupation?: string | null;
  dateOfBirth?: string | null;
  nationalities?: string[];
  preferredLanguage?: string;
};

export type ParentSortKey = 'name' | 'email' | 'children' | 'accountStatus' | 'lastLogin';

export type ParentsFilters = {
  page?: number;
  limit?: number;
  search?: string;
  studentId?: string;
  gradeLevel?: number;
  accountStatus?: 'invited' | 'active';
  active?: boolean;
  sortBy?: ParentSortKey;
  sortDir?: 'asc' | 'desc';
};

export type ParentListResponse = {
  data: ParentAdminRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export const parentsApi = {
  list: (params: ParentsFilters = {}) => {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') qs.append(key, String(value));
    }
    const s = qs.toString();
    return fetchApi<ParentListResponse>(`${PARENTS_PATH}${s ? `?${s}` : ''}`);
  },
  getById: (id: string) => fetchApi<ParentAdminRow>(`${PARENTS_PATH}/${id}`),
  create: (data: CreateParentData) =>
    fetchApi<ParentAdminRow>(PARENTS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateParentData) =>
    fetchApi<ParentAdminRow>(`${PARENTS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  resendInvite: (id: string) => fetchApi<{ success: true }>(`${PARENTS_PATH}/${id}/resend-invite`, { method: 'POST' }),
  updateActive: (id: string, active: boolean) =>
    fetchApi<ParentAdminRow>(`${PARENTS_PATH}/${id}/active`, { method: 'PATCH', body: JSON.stringify({ active }) }),
  linkStudent: (parentId: string, studentId: string) =>
    fetchApi<ParentAdminRow>(`${PARENTS_PATH}/${parentId}/students`, {
      method: 'POST',
      body: JSON.stringify({ studentId }),
    }),
  unlinkStudent: (parentId: string, studentId: string) =>
    fetchApi<ParentAdminRow>(`${PARENTS_PATH}/${parentId}/students/${studentId}`, { method: 'DELETE' }),
};
